import type { Event, ProjectEvent } from '@kalos/kalos-rpc';
import {
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  LoadingIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import {
  CheckCircledIcon,
  ChevronDownIcon,
  CrossCircledIcon,
  InfoCircledIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircledIcon,
} from '@radix-ui/react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTimesheetDepartmentListQuery } from '../../hooks/react-query/useTimesheetDepartmentQuery';
import { useValidateJobNumberQuery } from '../../hooks/react-query/useValidateJobNumberQuery';
import { useEvent } from '../../hooks/useEvent';
import { UnifiedJobSearch } from '../../modules/ComponentsLibrary/AdvancedSearch/UnifiedJobSearch';

export type UnifiedJobCheckStatus =
  | 'non-existing'
  | 'existing'
  | 'need-to-check'
  | 'loading'
  | 'none';

const staticEmptyFn = () => undefined;

type ProjectContext = {
  project: ProjectEvent;
  jobs: Event[];
  primaryJob: Event;
};

type ValidationRequest = {
  jobNumber: number;
  departmentId: number;
  fromInitial?: boolean;
};

function buildProjectContext(result: {
  project?: ProjectEvent;
  jobs?: Event[];
}): ProjectContext | null {
  const { project, jobs } = result;
  if (!project || !jobs?.length) return null;
  const primaryJob = jobs.find((j) => j.id === project.primaryEventId) ?? jobs[0];
  return jobs.length > 1 ? { project, jobs, primaryJob } : null;
}

export const UnifiedJobSelector = ({
  onChange,
  onJobSelect,
  value: externalValue,
  getStatusTestId,
  inputTestId,
  checkBtnTestId,
  disabled,
  advancedSearchContainerTestId,
  onStatusChange = staticEmptyFn,
  inputClassName,
  onFocus,
  onBlur,
  departmentId: departmentIdProp,
  omitArchivedJobs = false,
  id,
}: {
  onChange: (value: number) => void;
  onJobSelect?: (event: Event) => void;
  value: number | undefined;
  getStatusTestId?: (status: UnifiedJobCheckStatus) => string;
  inputTestId?: string;
  onStatusChange?: (status: UnifiedJobCheckStatus) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  checkBtnTestId?: string;
  disabled?: boolean;
  inputClassName?: string;
  advancedSearchContainerTestId?: string;
  id?: string;
  departmentId?: number;
  omitArchivedJobs?: boolean;
}) => {
  const [inputJobNumber, setInputJobNumber] = useState<string>(
    externalValue != null && externalValue !== 0 ? String(externalValue) : '',
  );
  const [checkStatus, _setCheckStatus] = useState<UnifiedJobCheckStatus>(
    externalValue != null && externalValue !== 0 ? 'loading' : 'none',
  );
  const [validationRequest, setValidationRequest] = useState<ValidationRequest | null>(null);
  const [jobDetails, setJobDetails] = useState<Event | null>(null);
  const [projectContext, setProjectContext] = useState<ProjectContext | null>(null);
  const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
  const [isJobSearchOpen, setIsJobSearchOpen] = useState(false);
  const [isJobInfoDialogOpen, setIsJobInfoDialogOpen] = useState(false);
  const [isApplyPopoverOpen, setIsApplyPopoverOpen] = useState(false);
  const initialValidatedRef = useRef(false);
  const lastSyncedExternalRef = useRef<number | undefined>(undefined);
  const inputJobNumberRef = useRef<string>(inputJobNumber);
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldContainerRef = useRef<HTMLDivElement>(null);
  const [dropdownContentWidth, setDropdownContentWidth] = useState<number | undefined>(undefined);
  const onChangeEvent = useEvent(onChange);
  const onStatusChangeEvent = useEvent(onStatusChange);

  const {
    data: validateResult,
    isSuccess,
    isFetching,
    isError,
  } = useValidateJobNumberQuery({
    jobNumber: validationRequest?.jobNumber ?? 0,
    departmentId: validationRequest?.departmentId ?? 0,
    enabled: validationRequest !== null && (validationRequest?.jobNumber ?? 0) > 0,
  });

  const { data: departmentsList } = useTimesheetDepartmentListQuery({
    filter: { isActive: 1 },
    enabled: true,
  });
  const departmentNameById: Record<number, string> =
    departmentsList?.results?.reduce(
      (acc, d) => {
        acc[d.id] = d.description || d.value || '';
        return acc;
      },
      {} as Record<number, string>,
    ) ?? {};

  const setCheckStatus = useCallback(
    (status: UnifiedJobCheckStatus) => {
      _setCheckStatus(status);
      onStatusChangeEvent(status);
    },
    [onStatusChangeEvent],
  );

  const applyJob = useCallback(
    (event: Event, projectCtx: ProjectContext | null, notifyParent = true) => {
      setJobDetails(event);
      setProjectContext(projectCtx);
      setIsJobMenuOpen(false);
      setCheckStatus('existing');
      if (notifyParent) {
        onChangeEvent(event.id);
        onJobSelect?.(event);
      }
      setInputJobNumber(String(event.id));
      setValidationRequest(null);
    },
    [onChangeEvent, onJobSelect, setCheckStatus],
  );

  const triggerValidate = useCallback(
    (jobNumber: number, fromInitial = false) => {
      if (!jobNumber || jobNumber <= 0) return;
      setCheckStatus('loading');
      setValidationRequest({
        jobNumber,
        departmentId: departmentIdProp ?? 0,
        fromInitial,
      });
    },
    [departmentIdProp, setCheckStatus],
  );

  // Sync from parent: when parent has no value (0/null), clear only if field is already empty (so we don't wipe user typing a new number). When parent has a value, only push it in when that value actually changed.
  useEffect(() => {
    if (externalValue == null || externalValue === 0) {
      lastSyncedExternalRef.current = undefined;
      const inputEmpty = inputJobNumberRef.current === '' || inputJobNumberRef.current === '0';
      const waitingForSubjobPick = projectContext && !jobDetails;
      if (inputEmpty && !waitingForSubjobPick) {
        inputJobNumberRef.current = '';
        setInputJobNumber('');
        setCheckStatus('none');
        setJobDetails(null);
        setProjectContext(null);
        setIsJobMenuOpen(false);
        setValidationRequest(null);
        initialValidatedRef.current = false;
      }
    } else {
      if (externalValue !== lastSyncedExternalRef.current) {
        lastSyncedExternalRef.current = externalValue;
        const next = String(externalValue);
        inputJobNumberRef.current = next;
        setInputJobNumber(next);
      }
    }
  }, [externalValue, projectContext, jobDetails, setCheckStatus, validationRequest]);

  // Initial load: validate pre-populated value once
  useEffect(() => {
    if (
      externalValue != null &&
      externalValue !== 0 &&
      !jobDetails &&
      !initialValidatedRef.current
    ) {
      initialValidatedRef.current = true;
      triggerValidate(externalValue, true);
    }
  }, [externalValue, jobDetails, triggerValidate]);

  // When validation settles: handle failure or success. Do not apply if user has cleared or changed the input (legacy: value and status stay in sync with user intent).
  useEffect(() => {
    if (!validationRequest || isFetching) return;

    const inputNow = inputJobNumberRef.current;
    const requestJobStr = String(validationRequest.jobNumber);
    if (inputNow === '' || inputNow === '0' || inputNow !== requestJobStr) return;

    if (isError || (isSuccess && !validateResult?.jobs?.length)) {
      setCheckStatus('non-existing');
      setJobDetails(null);
      setProjectContext(null);
      setValidationRequest(null);
      setIsJobSearchOpen(true);
      return;
    }

    if (!isSuccess || !validateResult?.jobs?.length) return;

    const { jobs: rawJobs } = validateResult;
    const jobs = omitArchivedJobs ? rawJobs.filter((j) => j.logJobStatus !== 'Archived') : rawJobs;

    if (!jobs.length) {
      setCheckStatus('non-existing');
      setJobDetails(null);
      setProjectContext(null);
      setValidationRequest(null);
      setIsJobSearchOpen(true);
      return;
    }

    const ctx = buildProjectContext({ ...validateResult, jobs });
    const requested = validationRequest.jobNumber;
    const notifyParent = !validationRequest.fromInitial;

    if (!ctx) {
      applyJob(jobs[0], null, notifyParent);
      return;
    }

    const costId = validateResult.costJobNumber ?? 0;
    const selectedJob =
      ctx.jobs.find((j) => j.id === requested) ??
      (costId > 0 ? ctx.jobs.find((j) => j.id === costId) : undefined) ??
      ctx.jobs.find((j) => {
        const logNum = Number(String(j.logJobNumber ?? '').replace(/\D/g, ''));
        return logNum > 0 && logNum === requested;
      }) ??
      ctx.primaryJob;
    applyJob(selectedJob, ctx, notifyParent);
    if (!validationRequest.fromInitial) {
      setIsJobMenuOpen(true);
    }
    setValidationRequest(null);
  }, [
    validationRequest,
    isFetching,
    isSuccess,
    isError,
    validateResult,
    applyJob,
    setCheckStatus,
    omitArchivedJobs,
  ]);

  useEffect(() => {
    const input = inputJobNumberRef.current;
    if (input === '' || input === '0') {
      return;
    }
    const job = validationRequest?.jobNumber ?? 0;
    if (job <= 0) return;
    if (isFetching && validationRequest) setCheckStatus('loading');
  }, [isFetching, validationRequest, setCheckStatus]);

  useEffect(() => {
    inputJobNumberRef.current = inputJobNumber;
  }, [inputJobNumber]);

  // Keep dropdown content width in sync with field container (e.g. on window resize)
  useEffect(() => {
    const el = fieldContainerRef.current;
    if (!el) return;
    const update = () => setDropdownContentWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || Number(value) === 0) {
      inputJobNumberRef.current = '';
      setInputJobNumber('');
      setCheckStatus('none');
      setJobDetails(null);
      setProjectContext(null);
      setIsJobMenuOpen(false);
      onChangeEvent(0);
      setValidationRequest(null);
      initialValidatedRef.current = false;
    } else {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        inputJobNumberRef.current = value;
        setInputJobNumber(value);
        setCheckStatus('need-to-check');
      }
    }
  };

  const onSearchBtnClick = () => {
    if (checkStatus === 'none' || checkStatus === 'existing' || checkStatus === 'non-existing') {
      setIsJobSearchOpen(true);
    } else if (checkStatus === 'need-to-check' && inputJobNumber) {
      triggerValidate(Number(inputJobNumber), false);
    }
    inputRef.current?.focus();
  };

  const onEventSelect = useCallback(
    (e: Event) => {
      if (omitArchivedJobs && e.logJobStatus === 'Archived') {
        setCheckStatus('non-existing');
        setIsJobSearchOpen(false);
        return;
      }
      setJobDetails(e);
      setProjectContext(null);
      setCheckStatus('existing');
      onChangeEvent(e.id);
      onJobSelect?.(e);
      setInputJobNumber(String(e.id));
      setIsJobSearchOpen(false);
    },
    [omitArchivedJobs, onChangeEvent, onJobSelect, setCheckStatus],
  );

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLButtonElement> = (e) => {
    if (
      e.relatedTarget?.id === 'check-job-btn' ||
      e.relatedTarget?.id === 'failed-check-state--apply-popover-trigger' ||
      e.relatedTarget?.id === 'job-menu-trigger'
    ) {
      return;
    }
    onBlur?.();
    // Don't re-validate when we already know the job doesn't exist (e.g. after closing search modal)
    if (checkStatus === 'non-existing') return;
    const num = Number(inputJobNumber);
    if (num > 0 && num !== externalValue) {
      triggerValidate(num, false);
    }
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      onSearchBtnClick();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').trim();
    const num = Number(text);
    if (!Number.isNaN(num) && num > 0) {
      e.preventDefault();
      const next = String(num);
      inputJobNumberRef.current = next;
      setInputJobNumber(next);
      setCheckStatus('loading');
      triggerValidate(num, false);
    }
  };

  const onSubjobSelect = useCallback(
    (eventIdStr: string) => {
      if (!projectContext) return;
      const job = projectContext.jobs.find((j) => j.id === Number(eventIdStr));
      if (job) applyJob(job, projectContext);
    },
    [projectContext, applyJob],
  );

  const onFailedCheckApplyClick = (value: number) => {
    setInputJobNumber(String(value));
    if (value === 0) setCheckStatus('none');
    else triggerValidate(value, false);
    setIsApplyPopoverOpen(false);
  };

  const shouldProposeApplyBtn =
    checkStatus === 'non-existing' &&
    typeof externalValue === 'number' &&
    Number(inputJobNumber) !== externalValue;

  const subjobOptions = projectContext
    ? (() => {
        const { primaryJob, jobs } = projectContext;
        const rest = jobs.filter((j) => j.id !== primaryJob.id);
        return [
          { event: primaryJob, isPrimary: true },
          ...rest.map((j) => ({ event: j, isPrimary: false })),
        ];
      })()
    : [];

  const confirmedLabel =
    jobDetails?.departmentId != null && jobDetails.departmentId !== 0
      ? departmentNameById[jobDetails.departmentId] ?? `Dept ${jobDetails.departmentId}`
      : null;

  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      <div
        ref={fieldContainerRef}
        className="border-input focus-within:ring-primary flex w-full min-w-0 flex-col items-stretch overflow-hidden rounded-lg border bg-white focus-within:ring-2 focus-within:ring-offset-0"
      >
        {confirmedLabel && (
          <div
            className="text-muted-foreground flex h-4 w-full shrink-0 overflow-hidden truncate px-2 pt-0.5 text-xs leading-none"
            title={confirmedLabel}
          >
            {confirmedLabel}
          </div>
        )}
        <div
          className={cn(
            'flex min-w-0 flex-nowrap items-stretch rounded-md p-0.5',
            confirmedLabel ? 'min-h-8 pt-0' : 'min-h-10',
          )}
        >
          <div className="min-w-0 flex-1 basis-0 overflow-hidden">
            <Input
              value={inputJobNumber}
              ref={inputRef}
              min={0}
              onPaste={onPaste}
              onFocus={onFocus}
              onBlur={handleBlur}
              onChange={onInputChange}
              onKeyDown={onInputKeyDown}
              type="number"
              className={cn(
                'h-full w-full min-w-0 max-w-full border-0 bg-white shadow-none focus-visible:ring-0',
                'rounded-md',
                inputClassName,
              )}
              disabled={disabled || checkStatus === 'loading'}
              inputMode="numeric"
              data-testid={inputTestId}
              id={id}
              placeholder="Enter job number and press Enter"
            />
          </div>
          <TooltipProvider>
            <div
              className={cn(
                'flex shrink-0 items-center justify-end gap-0.5 py-0.5 sm:gap-1 [&_button]:shrink-0',
                checkStatus === 'existing'
                  ? 'min-w-[5rem] [&_button]:h-8 [&_button]:min-w-8'
                  : 'min-w-[7rem] [&_button]:h-8 [&_button]:min-w-8',
                // 44px on touch screens, as every control (design system section 6).
                'touch:py-0 touch:[&_button]:h-11 touch:[&_button]:min-w-11',
              )}
            >
              <Popover open={isApplyPopoverOpen} onOpenChange={setIsApplyPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    id="failed-check-state--apply-popover-trigger"
                    data-testid={getStatusTestId?.(checkStatus)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!shouldProposeApplyBtn}
                    className={cn(
                      shouldProposeApplyBtn
                        ? buttonVariants({ variant: 'outline', className: 'px-2' })
                        : 'scale-150 p-1',
                    )}
                  >
                    <UnifiedCheckStatusIndicator status={checkStatus} />
                  </button>
                </PopoverTrigger>
                {typeof externalValue === 'number' && (
                  <PopoverContent className="flex items-center justify-between gap-2 p-2 text-sm">
                    <p className="min-w-max">Current applied value: {externalValue}</p>
                    <Button
                      data-testid="unified-job-selector-apply-existing-value-btn"
                      onClick={() => onFailedCheckApplyClick(externalValue)}
                      size="sm"
                    >
                      Apply
                    </Button>
                  </PopoverContent>
                )}
              </Popover>
              {checkStatus !== 'existing' && (
                <Button
                  variant="outline"
                  tabIndex={0}
                  type="button"
                  className="px-2"
                  onBlur={handleBlur}
                  disabled={checkStatus === 'loading' || disabled}
                  onClick={onSearchBtnClick}
                  data-testid={checkBtnTestId}
                  id="check-job-btn"
                >
                  <MagnifyingGlassIcon />
                  <span className="sr-only">Check job</span>
                </Button>
              )}
              <DropdownMenu
                open={isJobMenuOpen}
                onOpenChange={(open) => {
                  if (open && fieldContainerRef.current) {
                    setDropdownContentWidth(fieldContainerRef.current.offsetWidth);
                  }
                  setIsJobMenuOpen(open);
                }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        tabIndex={0}
                        type="button"
                        className="px-2"
                        onBlur={handleBlur}
                        id="job-menu-trigger"
                      >
                        <ChevronDownIcon className="size-4" />
                        <span className="sr-only">Job options</span>
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Job options — details and job selection</TooltipContent>
                </Tooltip>
                <DropdownMenuContent
                  align="end"
                  className="min-w-0"
                  style={{ width: dropdownContentWidth ?? undefined }}
                  loop
                  onKeyDown={(e) => {
                    // Tab/Shift+Tab: navigate within menu items instead of closing
                    if (e.key === 'Tab') {
                      const items = (e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
                        '[role="menuitem"]',
                      );
                      if (items.length === 0) return;
                      const current = document.activeElement;
                      const idx = Array.from(items).findIndex((el) => el === current);
                      if (idx === -1) return;
                      e.preventDefault();
                      if (e.shiftKey) {
                        const nextIdx = idx <= 0 ? items.length - 1 : idx - 1;
                        items[nextIdx]?.focus();
                      } else {
                        const nextIdx = idx >= items.length - 1 ? 0 : idx + 1;
                        items[nextIdx]?.focus();
                      }
                    }
                  }}
                >
                  {jobDetails && (
                    <DropdownMenuItem
                      onClick={() => {
                        setIsJobInfoDialogOpen(true);
                        setIsJobMenuOpen(false);
                      }}
                    >
                      <InfoCircledIcon className="mr-2 size-4" />
                      Show Job Details
                    </DropdownMenuItem>
                  )}
                  {jobDetails && projectContext && subjobOptions.length > 0 && (
                    <DropdownMenuSeparator />
                  )}
                  {projectContext && subjobOptions.length > 0 && (
                    <>
                      {subjobOptions.map(({ event, isPrimary }) => {
                        const tradeLabel =
                          departmentNameById[event.departmentId] ||
                          (event.departmentId ? `Dept ${event.departmentId}` : '');
                        const label = isPrimary
                          ? `${event.logJobNumber || event.id} (Project primary)${tradeLabel ? ` — ${tradeLabel}` : ''}`
                          : `${event.logJobNumber || event.id}${tradeLabel ? ` — ${tradeLabel}` : ''}`;
                        return (
                          <DropdownMenuItem
                            key={event.id}
                            onClick={() => onSubjobSelect(String(event.id))}
                            className={cn(
                              jobDetails?.id === event.id && 'bg-primary text-primary-foreground',
                            )}
                          >
                            {label}
                          </DropdownMenuItem>
                        );
                      })}
                    </>
                  )}
                  {!jobDetails && !projectContext && (
                    <p className="text-muted-foreground px-2 py-1.5 text-sm">No job selected</p>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </TooltipProvider>
        </div>
      </div>

      <Dialog open={isJobSearchOpen} onOpenChange={setIsJobSearchOpen}>
        <DialogContent
          showClose={false}
          data-testid={advancedSearchContainerTestId}
          className="h-svh w-screen max-w-none overflow-auto p-0"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <UnifiedJobSearch
            accounting={false}
            onSelectEvent={onEventSelect}
            onClose={() => setIsJobSearchOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isJobInfoDialogOpen} onOpenChange={setIsJobInfoDialogOpen}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Job Information</h2>
            {projectContext && jobDetails && (
              <div className="bg-muted/50 rounded-md border p-3 text-sm">
                <p className="text-muted-foreground font-medium">Part of project</p>
                <p>{projectContext.primaryJob.logJobNumber || projectContext.primaryJob.id}</p>
                {jobDetails.id !== projectContext.primaryJob.id && (
                  <>
                    <p className="text-muted-foreground mt-2 font-medium">Selected job</p>
                    <p>{jobDetails.logJobNumber || jobDetails.id}</p>
                  </>
                )}
              </div>
            )}
            {jobDetails && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">
                    {jobDetails.customer?.businessname ? 'Business Name' : 'Customer'}
                  </h3>
                  <p className="text-sm">
                    {jobDetails.customer?.businessname ||
                      `${jobDetails.customer?.firstname || ''} ${jobDetails.customer?.lastname || ''}`.trim() ||
                      'N/A'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Property</h3>
                  <p className="text-sm">{jobDetails.property?.address || 'N/A'}</p>
                  <p className="text-sm">
                    {jobDetails.property?.city && jobDetails.property?.zip
                      ? `${jobDetails.property.city}, ${jobDetails.property.zip}`
                      : jobDetails.property?.city || jobDetails.property?.zip || 'N/A'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Job Type</h3>
                  <p className="text-sm">{jobDetails.jobType || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Department</h3>
                  <p className="text-sm">
                    {jobDetails.departmentId != null && jobDetails.departmentId !== 0
                      ? departmentNameById[jobDetails.departmentId] ??
                        `Dept ${jobDetails.departmentId}`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Brief Description</h3>
                  <p className="text-sm">{jobDetails.name || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Services Needed</h3>
                  <p className="text-sm">{jobDetails.description || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Job Notes</h3>
                  <p className="text-sm">{jobDetails.logNotes || 'N/A'}</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const UnifiedCheckStatusIndicator = ({ status }: { status: UnifiedJobCheckStatus }) => {
  switch (status) {
    case 'none':
      return null;
    case 'existing':
      return <CheckCircledIcon className="text-green-500" />;
    case 'non-existing':
      return <CrossCircledIcon className="text-primary" />;
    case 'loading':
      return <LoadingIcon className="size-[15px] text-blue-500" />;
    case 'need-to-check':
      return <QuestionMarkCircledIcon className="text-gray-500" />;
  }
};
